import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { BillingInfo, PaymentMethod, SimplePickerList, useBillingContext } from '@shipstation/financial-services-ui-components';
import type { BillingInfoData } from '@shipstation/financial-services-ui-components';

import { FlexColumn } from 'ui-components/components/flex/column';
import { Gap } from 'ui-components/components/flex/gap';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Text, TextType } from 'ui-components/components/text';
import { noop } from 'ui-components/default-props/noop';

import { useFsuiContext } from '../../../financial-services-ui/fsui-context-provider';
import type { PaymentMethodDetails } from '../../../financial-services-ui/types/fsui';
import { usePaymentAndSubscriptionMegaContext } from '../../../pages/settings/payment-and-subscription/context';
import Global from '../../../store/global-selectors';
import { usePaymentMethodCreation } from '../hooks/use-payment-method-creation';
import { messages } from '../messages';
import style from './payment-method-section.module.less';

type PaymentMethodSectionProps = {
  isVisible: boolean;
  onPaymentMethodCreated: (paymentMethodId: string, billingInfo?: BillingInfoData) => void;
  onSubmitFunctionReady?: (submitFn: (() => void) | null) => void;
};

export const PaymentMethodSection = ({ isVisible, onPaymentMethodCreated, onSubmitFunctionReady }: PaymentMethodSectionProps) => {
  const intl = useIntl();
  const { preloadedSession, isPreloadingSession, onPaymentCompleted, onError } = useFsuiContext();
  const { billingInfo } = useBillingContext();

  const context = usePaymentAndSubscriptionMegaContext();
  const { paymentMethodDetails } = context;
  const reloadData = (context as { reloadData?: () => Promise<[unknown, PaymentMethodDetails[] | undefined]> }).reloadData;

  const sellerDefaultCurrencyCode = useSelector(Global.selectSellerDefaultCurrency);
  const locale = intl.locale;

  const [selectedType, setSelectedType] = useState<PaymentMethod | null>(null);
  const [initialPaymentMethodCount] = useState(paymentMethodDetails.length);

  const setSubmitFunction = useCallback(
    (fn: (() => void) | null) => {
      onSubmitFunctionReady?.(fn);
    },
    [onSubmitFunctionReady],
  );

  const handleError = useCallback(() => {
    if (!preloadedSession) {
      return;
    }
    onError(new Error('Payment method creation failed'));
  }, [onError, preloadedSession]);

  // Use custom hook for payment method creation logic
  const { handlePaymentSuccess, updateBillingInfoRef } = usePaymentMethodCreation({
    initialPaymentMethodCount,
    reloadData,
    onSuccess: onPaymentMethodCreated,
    onError: handleError,
  });

  // Keep billing info ref updated
  useEffect(() => {
    updateBillingInfoRef(billingInfo);
  }, [billingInfo, updateBillingInfoRef]);

  const handlePaymentCompleted = useCallback(
    (result: unknown) => {
      onPaymentCompleted(result as Record<string, unknown>, handlePaymentSuccess, handleError);
    },
    [onPaymentCompleted, handlePaymentSuccess, handleError],
  );

  const creditCardConfig = useMemo(
    () => ({
      currencyCode: sellerDefaultCurrencyCode,
      inputBorderRadius: '4px',
      inputHeight: '32px',
      locale,
      onError: handleError,
      onPaymentCompleted: handlePaymentCompleted,
      returnUrl: window.location.origin,
      submitPayment: setSubmitFunction,
      transactionCategory: undefined,
    }),
    [handleError, handlePaymentCompleted, setSubmitFunction, locale, sellerDefaultCurrencyCode],
  );

  const payByBankUsConfig = useMemo(
    () => ({
      currencyCode: sellerDefaultCurrencyCode,
      locale,
      session: preloadedSession,
      onError: handleError,
      onPaymentCompleted: noop,
      returnUrl: window.location.href,
      submitPayment: setSubmitFunction,
      transactionCategory: undefined,
    }),
    [handleError, locale, preloadedSession, setSubmitFunction, sellerDefaultCurrencyCode],
  );

  // Set default payment type when session loads
  useEffect(() => {
    if (!isPreloadingSession && !selectedType) {
      setSelectedType(preloadedSession?.eligiblePaymentMethodTypes?.[0] || PaymentMethod.CreditCard);
    }
  }, [isPreloadingSession, preloadedSession, selectedType]);

  if (!isVisible) {
    return null;
  }

  return (
    <FlexColumn gap={Gap.Space16}>
      <Text textType={TextType.H4} value={messages.paymentMethodSectionTitle} />

      {isPreloadingSession ? (
        <LoadingIndicator className={style.loader} />
      ) : (
        <>
          <SimplePickerList
            creditCardConfig={creditCardConfig}
            currencyCode={sellerDefaultCurrencyCode}
            locale={locale}
            payByBankConfig={payByBankUsConfig}
            paymentTypes={preloadedSession?.eligiblePaymentMethodTypes || [PaymentMethod.CreditCard]}
            selectedType={selectedType ?? undefined}
            setSelectedType={setSelectedType}
          />
          <BillingInfo setPerformFormValidation={noop} />
        </>
      )}
    </FlexColumn>
  );
};
